import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  FileText, 
  Save,
  ArrowRight,
  Plus,
  X,
  AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContractTemplateFormPage = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const isEditMode = !!templateId;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    contract_type: 'consultation_agreement',
    description_ar: '',
    description_en: '',
    arabic_text: '',
    english_text: '',
    fillable_fields: [],
    is_active: true
  });

  const [newField, setNewField] = useState('');

  const contractTypes = [
    { value: 'service_agreement', label_ar: 'عقد خدمات', label_en: 'Service Agreement' },
    { value: 'consultation_agreement', label_ar: 'عقد استشارات', label_en: 'Consultation Agreement' },
    { value: 'retainer_agreement', label_ar: 'عقد أتعاب', label_en: 'Retainer Agreement' },
    { value: 'legal_representation', label_ar: 'عقد توكيل', label_en: 'Legal Representation' },
    { value: 'confidentiality_agreement', label_ar: 'اتفاقية سرية', label_en: 'Confidentiality Agreement' },
    { value: 'partnership_agreement', label_ar: 'عقد شراكة', label_en: 'Partnership Agreement' },
    { value: 'other', label_ar: 'أخرى', label_en: 'Other' }
  ];

  useEffect(() => {
    if (isEditMode) {
      fetchTemplate();
    }
  }, [templateId]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/contracts/templates/${templateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFormData(response.data);
    } catch (err) {
      console.error('خطأ في جلب القالب:', err);
      setError('فشل في تحميل القالب');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addField = () => {
    if (newField.trim() && !formData.fillable_fields.includes(newField.trim())) {
      setFormData(prev => ({
        ...prev,
        fillable_fields: [...prev.fillable_fields, newField.trim()]
      }));
      setNewField('');
    }
  };

  const removeField = (field) => {
    setFormData(prev => ({
      ...prev,
      fillable_fields: prev.fillable_fields.filter(f => f !== field)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      if (isEditMode) {
        await axios.put(
          `${BACKEND_URL}/api/contracts/templates/${templateId}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${BACKEND_URL}/api/contracts/templates`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      navigate('/admin/contracts/templates');
    } catch (err) {
      console.error('خطأ في حفظ القالب:', err);
      setError(err.response?.data?.detail || 'حدث خطأ في حفظ القالب');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C8A870] mx-auto"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4" dir="rtl">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-[#C8A870] to-[#b8986a] p-3 rounded-lg">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#1e3a8a]">
                  {isEditMode ? 'تعديل قالب العقد' : 'إضافة قالب جديد'}
                </h1>
                <p className="text-gray-600">
                  {isEditMode ? 'تعديل معلومات القالب' : 'إنشاء قالب عقد جديد'}
                </p>
              </div>
            </div>
            
            <button
              onClick={() => navigate('/admin/contracts/templates')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowRight className="w-5 h-5" />
              رجوع
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border-r-4 border-red-500 p-4 mb-6 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <p className="font-semibold text-red-800">خطأ</p>
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                اسم القالب (عربي) *
              </label>
              <input
                type="text"
                name="name_ar"
                value={formData.name_ar}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                placeholder="مثال: عقد استشارات قانونية"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                اسم القالب (إنجليزي)
              </label>
              <input
                type="text"
                name="name_en"
                value={formData.name_en}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                placeholder="Example: Legal Consultation Agreement"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                نوع العقد *
              </label>
              <select
                name="contract_type"
                value={formData.contract_type}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
              >
                {contractTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label_ar} - {type.label_en}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                الحالة
              </label>
              <div className="flex items-center gap-3 mt-3">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-[#C8A870] border-gray-300 rounded focus:ring-[#C8A870]"
                />
                <span className="text-gray-700">قالب نشط (يظهر في قائمة الإنشاء)</span>
              </div>
            </div>
          </div>

          {/* Descriptions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                الوصف (عربي)
              </label>
              <textarea
                name="description_ar"
                value={formData.description_ar}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                placeholder="وصف مختصر للعقد..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                الوصف (إنجليزي)
              </label>
              <textarea
                name="description_en"
                value={formData.description_en}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                placeholder="Brief description..."
              />
            </div>
          </div>

          {/* Contract Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              نص العقد (عربي) *
            </label>
            <p className="text-sm text-gray-500 mb-2">
              استخدم {`{{field_name}}`} للحقول القابلة للتعبئة. مثال: {`{{client_name}}`}
            </p>
            <textarea
              name="arabic_text"
              value={formData.arabic_text}
              onChange={handleInputChange}
              required
              rows={15}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870] font-mono text-sm"
              placeholder="أدخل نص العقد العربي هنا..."
            />
          </div>

          {/* Fillable Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              الحقول القابلة للتعبئة
            </label>
            <p className="text-sm text-gray-500 mb-3">
              أضف الحقول التي يمكن تعبئتها تلقائياً (بدون {`{{}}`})
            </p>
            
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newField}
                onChange={(e) => setNewField(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addField())}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                placeholder="مثال: client_name"
              />
              <button
                type="button"
                onClick={addField}
                className="px-4 py-2 bg-[#C8A870] text-white rounded-lg hover:bg-[#b8986a]"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {formData.fillable_fields.map(field => (
                <div
                  key={field}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg"
                >
                  <span className="font-mono text-sm">{field}</span>
                  <button
                    type="button"
                    onClick={() => removeField(field)}
                    className="hover:bg-blue-200 rounded p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {formData.fillable_fields.length === 0 && (
              <p className="text-gray-400 text-sm italic">لم يتم إضافة حقول بعد</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/admin/contracts/templates')}
              className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-[#C8A870] to-[#b8986a] text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Save className="w-5 h-5" />
              {loading ? 'جاري الحفظ...' : (isEditMode ? 'حفظ التعديلات' : 'إضافة القالب')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContractTemplateFormPage;
